/**
 * Reusable button component
 */

import type { CSSProperties } from 'react';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  loading?: boolean;
  /** Shown beside the spinner while loading, e.g. "Adding officers…". Without it the
   * spinner shows alone. */
  loadingText?: string;
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  style?: CSSProperties;
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  loading = false,
  loadingText,
  size = 'medium',
  fullWidth = false,
  style,
}: ButtonProps) {
  const sizeStyles: Record<typeof size, CSSProperties> = {
    small: {
      padding: '6px 12px',
      fontSize: '12px',
    },
    medium: {
      padding: '10px 20px',
      fontSize: '14px',
    },
    large: {
      padding: '14px 28px',
      fontSize: '16px',
    },
  };

  const variantStyles: Record<typeof variant, CSSProperties> = {
    primary: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' },
    secondary: { background: '#f5f5f5', color: '#333' },
    danger: { background: '#dc3545', color: 'white' },
  };

  const buttonStyle: CSSProperties = {
    ...sizeStyles[size],
    position: 'relative',
    fontWeight: 600,
    border: 'none',
    borderRadius: '6px',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s',
    width: fullWidth ? '100%' : 'auto',
    ...variantStyles[variant],
    ...style, // caller's style wins over variant
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      style={buttonStyle}
    >
      {/* Children stay mounted (just hidden) while loading so the button keeps its
          natural size instead of shrinking/growing to fit a "Loading..." label. */}
      <span style={{ visibility: loading ? 'hidden' : 'visible' }}>{children}</span>
      {loading && (
        <span
          role="status"
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 14,
              height: 14,
              flexShrink: 0,
              borderRadius: '50%',
              border: '2px solid rgba(0,0,0,0.15)',
              borderTopColor: 'currentColor',
              animation: 'btn-spin 0.6s linear infinite',
            }}
          />
          {loadingText && <span>{loadingText}</span>}
        </span>
      )}
    </button>
  );
}
